import { FC, useEffect, useState } from "react";
import capitalize from "lodash/capitalize";
import {
  Button,
  Chip,
  Container,
  Group,
  Space,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";

import {
  FindingTriageReason,
  FindingTriageState,
} from "@protos/common/v1/common";
import { IssuesFilter, IssueType } from "@protos/issues/v1/issues";
import {
  useCreateNangoTicketMutation,
  useCreateTicketMutation,
  useOrg,
  usePrimaryNangoTicketingInstance,
  usePrimaryTicketingInstance,
} from "@shared/hooks";
import {
  FindingsFilters,
  TriageAction,
  TriageReason,
  TriageState,
  TriageStateColorMapping,
} from "@shared/types";

import { useTriage } from "../../hooks/useTriage";

import { IgnoredTriageComponent } from "./IgnoredTriageComponent";
import { IgnoreFutureFindingsOptionsProps } from "./IgnoreFutureFindingsOptions";
import { TriageActionsComponent } from "./TriageActionsComponent";

const TRIAGE_ACTIONS = {
  reopened: "Open",
  reviewing: "Reviewing",
  fixing: "Fixing",
  ignored: "Ignored",
  fixed: "Fixed",
};
const TRIAGE_TO_PROTO_VALUES = {
  ignored: FindingTriageState.IGNORED,
  reopened: FindingTriageState.REOPENED,
  reviewing: FindingTriageState.REVIEWING,
  fixing: FindingTriageState.FIXING,
};

const triageReasonProtoMap: { [key in TriageReason]: FindingTriageReason } = {
  false_positive: FindingTriageReason.FALSE_POSITIVE,
  acceptable_risk: FindingTriageReason.ACCEPTABLE_RISK,
  no_time: FindingTriageReason.NO_TIME,
};

export interface ImprovedTriageOptionsProps {
  issueType: IssueType;
  findingIds?: number[];
  triageFilter: IssuesFilter | null;
  issueParentIds: number[];
  ruleIds: number[];
  currentTriageState: TriageState;
  refetchAfterTriage?: boolean;
  isLoadingIgnoreOptions?: boolean;
  onSaveClick?: () => void; // closes the triage popover
  filterOverrides?: FindingsFilters;
  totalCount?: number;
  disableTicketing?: boolean;
  handleSave?: () => void; // save changed status of finding
  ignoreOptions?: IgnoreFutureFindingsOptionsProps;
  selectAllFindings?: boolean;
}

export const ImprovedTriageOptions: FC<ImprovedTriageOptionsProps> = ({
  issueType,
  onSaveClick = () => {},
  currentTriageState,
  findingIds,
  totalCount,
  filterOverrides = {},
  refetchAfterTriage,
  triageFilter,
  issueParentIds,
  ruleIds,
  ignoreOptions,
  isLoadingIgnoreOptions,
  selectAllFindings = false,
}) => {
  const [org] = useOrg();
  const {
    data: primaryTicketingInstance,
    isLoading: isPrimaryTicketingInstanceLoading,
  } = usePrimaryTicketingInstance();

  const {
    data: primaryNangoTicketingInstance,
    isLoading: isPrimaryNangoTicketingInstanceLoading,
  } = usePrimaryNangoTicketingInstance();
  const { mutate: createTicket, isPending: createTicketLoading } =
    useCreateTicketMutation();
  const {
    mutateAsync: createNangoTicket,
    isPending: createNangoTicketLoading,
  } = useCreateNangoTicketMutation(issueType);

  const numFindingsToTriage = totalCount ?? findingIds?.length ?? 0;
  const integrationName = primaryNangoTicketingInstance
    ? capitalize(primaryNangoTicketingInstance.instance_type)
    : capitalize(primaryTicketingInstance?.integration_name);
  const isConfigured =
    !isPrimaryTicketingInstanceLoading &&
    !isPrimaryNangoTicketingInstanceLoading &&
    ((primaryNangoTicketingInstance &&
      primaryNangoTicketingInstance.is_configured) ||
      (!primaryNangoTicketingInstance &&
        primaryTicketingInstance?.is_configured));

  const chooseDefaultTriageAction = (
    currentState: TriageState
  ): TriageAction => {
    switch (currentState) {
      case "untriaged":
        return "reviewing";
      case "reopened":
        return "reviewing";
      case "reviewing":
        return "reviewing";
      case "fixing":
        return "fixing";
      case "ignored":
        return "ignored";
    }
  };

  const isNoOp = (currentState: TriageState, triageAction: TriageAction) => {
    if (triageAction === "reopened") {
      return currentState === "reopened" || currentState === "untriaged";
    } else {
      return triageAction === currentState;
    }
  };

  const [triageAction, setTriageAction] = useState<TriageAction>(
    chooseDefaultTriageAction(currentTriageState)
  );
  const [note, setNote] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const { bulkTriage, bulkTriageFindingsV2, triageFinding } = useTriage(
    issueType,
    refetchAfterTriage
  );
  const [triageReason, setTriageReason] = useState<TriageReason>();
  const [projectId, setProjectId] = useState<string>(
    primaryNangoTicketingInstance?.project_id ?? ""
  );

  const [createTicketChecked, setCreateTicketChecked] = useState(false);
  const [multiIssueTickets, setMultiIssueTickets] = useState(true); // only supported on nango

  useEffect(() => {
    setProjectId(primaryNangoTicketingInstance?.project_id ?? "");
  }, [primaryNangoTicketingInstance?.project_id]);

  const handleTicketCreation = async () => {
    if (org && primaryTicketingInstance && !primaryNangoTicketingInstance) {
      onSaveClick();
      showNotification({
        title: "Ticket creation in progress",
        // Ticket creation is handled asynchronously using a Celery task. Triage is performed after ticket creation,
        // and the two processes are paired together. This is not perfect, but it is more to encourage users to avoid
        // navigating away from the page before ticket creation is complete. This ensures a higher chance that triage state updates
        // are applied correctly and not affected by premature navigation.
        message:
          "Tickets are still processing in the background. Do not navigate away from the page until the ticket creation is complete.",
        color: "blue",
      });
      createTicket(
        {
          deploymentId: org.id,
          ticketingId: primaryTicketingInstance.id,
          issueIds: findingIds,
          filterOverrides,
        },
        {
          onSuccess: () => {
            onSaveClick();
          },
        }
      );
    } else if (org && primaryNangoTicketingInstance) {
      onSaveClick();
      showNotification({
        title: "Ticket creation in progress",
        // Ticket creation is handled asynchronously using a Celery task. Triage is performed after ticket creation,
        // and the two processes are paired together. This is not perfect, but it is more to encourage users to avoid
        // navigating away from the page before ticket creation is complete. This ensures a higher chance that triage state updates
        // are applied correctly and not affected by premature navigation.
        message:
          "Tickets are still processing in the background. Do not navigate away from the page until the ticket creation is complete.",
        color: "blue",
      });

      await createNangoTicket(
        {
          deploymentId: org.id,
          ticketingId: primaryNangoTicketingInstance.id,
          issueType,
          issueIds: findingIds,
          filterOverrides,
          multiIssueTickets: multiIssueTickets,
          projectId,
        },
        {
          onSuccess: () => {
            onSaveClick();
          },
        }
      );
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    if (createTicketChecked) {
      await handleTicketCreation();
    }

    if (triageFilter) {
      if (selectAllFindings) {
        // If select all, do not merge rule ids or issue ids
        await bulkTriageFindingsV2({
          filter: triageFilter,
          triageState: TRIAGE_TO_PROTO_VALUES[triageAction!],
          triageReason: triageReason && triageReasonProtoMap[triageReason],
          note,
        });
      } else {
        // Triaging together would filter by issueParentIds AND ruleIds
        // resulting in no rows, so perform separate requests.
        if (issueParentIds.length > 0) {
          await bulkTriageFindingsV2({
            filter: {
              ...triageFilter,
              issueParentIds: issueParentIds,
            },
            triageState: TRIAGE_TO_PROTO_VALUES[triageAction!],
            triageReason: triageReason && triageReasonProtoMap[triageReason],
            note,
          });
        }
        if (ruleIds.length > 0) {
          await bulkTriageFindingsV2({
            filter: {
              ...triageFilter,
              ruleIds,
            },
            triageState: TRIAGE_TO_PROTO_VALUES[triageAction!],
            triageReason: triageReason && triageReasonProtoMap[triageReason],
            note,
          });
        }
      }

      setIsLoading(false);
      onSaveClick();
      setNote("");
      return;
    }

    // Triage findings- success/failure notifications are handled in hook
    if (
      findingIds === undefined ||
      findingIds.length > 1 ||
      filterOverrides.rule_id !== undefined
    ) {
      // triaging everything in the current set of filters
      await bulkTriage(
        triageAction!,
        note,
        triageReason,
        findingIds,
        filterOverrides
      );
    } else {
      // triaging just one finding
      await triageFinding(
        findingIds[0],
        triageAction!,
        note,
        undefined,
        triageReason
      );
    }

    setIsLoading(false);
    // should close the popover
    onSaveClick();
    setNote("");
  };

  const renderTriageComponent = () => {
    switch (triageAction) {
      case "ignored":
        return (
          <IgnoredTriageComponent
            note={note}
            setNote={setNote}
            setTriageReason={setTriageReason}
            triageReason={triageReason}
            ignoreOptions={ignoreOptions}
            isLoadingIgnoreOptions={isLoadingIgnoreOptions}
          />
        );
      case "reopened":
        return (
          <TriageActionsComponent
            note={note}
            setNote={setNote}
            integrationName={integrationName}
            isConfigured={isConfigured}
            isV3Integration={!!primaryNangoTicketingInstance}
            numFindingsToTriage={numFindingsToTriage}
            createTicketChecked={createTicketChecked}
            setCreateTicketChecked={setCreateTicketChecked}
            hasMultiIssueTicketsAvailable={!!primaryNangoTicketingInstance}
            multiIssueTickets={multiIssueTickets}
            setMultiIssueTickets={setMultiIssueTickets}
            projectId={projectId}
            setProjectId={setProjectId}
          />
        );
      case "reviewing":
        return (
          <TriageActionsComponent
            note={note}
            setNote={setNote}
            integrationName={integrationName}
            isConfigured={isConfigured}
            isV3Integration={!!primaryNangoTicketingInstance}
            numFindingsToTriage={numFindingsToTriage}
            createTicketChecked={createTicketChecked}
            setCreateTicketChecked={setCreateTicketChecked}
            hasMultiIssueTicketsAvailable={!!primaryNangoTicketingInstance}
            multiIssueTickets={multiIssueTickets}
            setMultiIssueTickets={setMultiIssueTickets}
            projectId={projectId}
            setProjectId={setProjectId}
          />
        );
      case "fixing":
        return (
          <TriageActionsComponent
            note={note}
            setNote={setNote}
            integrationName={integrationName}
            isConfigured={isConfigured}
            isV3Integration={!!primaryNangoTicketingInstance}
            numFindingsToTriage={numFindingsToTriage}
            createTicketChecked={createTicketChecked}
            setCreateTicketChecked={setCreateTicketChecked}
            hasMultiIssueTicketsAvailable={!!primaryNangoTicketingInstance}
            multiIssueTickets={multiIssueTickets}
            setMultiIssueTickets={setMultiIssueTickets}
            projectId={projectId}
            setProjectId={setProjectId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container p="md">
      <Stack gap="8">
        <Text fz="xs" fw="700">
          New status
        </Text>
        <Chip.Group
          value={triageAction}
          onChange={(value) => {
            setTriageReason(undefined);
            setTriageAction(value as TriageAction);

            if (ignoreOptions) {
              ignoreOptions.setDisableRule(false);
              ignoreOptions.setIgnoreOption("");
            }
          }}
        >
          <Group gap={8}>
            {Object.entries(TRIAGE_ACTIONS).map(([action, text]) => (
              <Tooltip
                disabled={action !== "fixed"}
                key={action}
                refProp="rootRef"
                position="top"
                arrowPosition="side"
                label={
                  action === "fixed" &&
                  "Semgrep automatically marks findings as fixed when they are no longer detected in scans."
                }
              >
                <Chip
                  key={action}
                  value={action}
                  size="xs"
                  data-testid="TriageActionChip"
                  color={TriageStateColorMapping[triageAction!]}
                  disabled={action === "fixed"} // Disable the chip if its value matches currentTriageState
                  styles={
                    action === triageAction
                      ? { label: { fontWeight: 600 } }
                      : {}
                  }
                >
                  {text}
                </Chip>
              </Tooltip>
            ))}
          </Group>
        </Chip.Group>

        {renderTriageComponent()}
        {createTicketChecked &&
          (createTicketLoading || createNangoTicketLoading) &&
          (!findingIds || findingIds.length > 5) && (
            // Ticket creation is handled asynchronously using a Celery task. Triage is performed after ticket creation,
            // and the two processes are paired together. This is not perfect, but it is more to encourage users to avoid
            // navigating away from the page before ticket creation is complete. This ensures a higher chance that triage state updates
            // are applied correctly and not affected by premature navigation.
            <Text fz="xs" c="dimmed">
              This operation may take some time. Do not navigate away from the
              page until the ticket creation is complete.
            </Text>
          )}
        <Space h="20" />
        <Group justify="flex-end">
          <Button
            data-testid="ChangeStatusButton"
            size="compact-xs"
            onClick={handleSave}
            loading={isLoading}
            style={{ width: "100px" }}
            disabled={
              !note &&
              !createTicketChecked &&
              isNoOp(currentTriageState, triageAction)
            }
          >
            Submit
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};
